import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import AccessManager from '../utils/AccessManager'
import CONSTANTS from '../utils/constants'

declare global {
  interface Window {
    HubSpotConversations?: {
      widget: {
        open: () => void
        close: () => void
        load: (options?: { widgetOpen?: boolean }) => void
        refresh: (options?: { openToNewThread?: boolean }) => void
        remove: () => void
        status: () => { loaded: boolean; pending: boolean }
      }
      on: (event: string, callback: () => void) => void
      off: (event: string, callback: () => void) => void
    }
    hsConversationsOnReady?: Array<() => void>
    hsConversationsSettings?: {
      loadImmediately?: boolean
      identificationToken?: string
      identificationEmail?: string
    }
  }
}

interface HubspotContextType {
  contactSupport: () => void
  isInitialized: boolean
}

const HubspotContext = createContext<HubspotContextType | null>(null)

export function useHubspot() {
  const context = useContext(HubspotContext)
  if (!context) {
    throw new Error('useHubspot must be used within HubspotProvider')
  }
  return context
}

export default function HubspotProvider({ children }: { children: React.ReactNode }) {
  const [visitorToken, setVisitorToken] = useState<string | null>(null)
  const [scriptReady, setScriptReady] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const scriptLoadedRef = useRef(false)
  const fetchedRef = useRef(false)

  const fetchVisitorToken = useCallback(async () => {
    if (fetchedRef.current) return
    const idToken = AccessManager.idToken
    if (!idToken) {
      setScriptReady(true)
      return
    }

    fetchedRef.current = true

    try {
      const response = await fetch('/api/hubspot', {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
      })
      if (!response.ok) {
        console.warn('HubSpot visitor token fetch failed — loading anonymously')
        setScriptReady(true)
        return
      }
      const data = await response.json()
      if (data.anonymous || !data.token) {
        setScriptReady(true)
        return
      }
      setVisitorToken(data.token)
      setScriptReady(true)
    } catch (error) {
      console.error('Failed to fetch HubSpot visitor token:', error)
      fetchedRef.current = false
      setScriptReady(true)
    }
  }, [])

  useEffect(() => {
    if (AccessManager.idToken) {
      fetchVisitorToken()
    }

    const handleTokenSet = () => fetchVisitorToken()
    window.addEventListener('kenai:auth-token-set', handleTokenSet)
    return () => window.removeEventListener('kenai:auth-token-set', handleTokenSet)
  }, [fetchVisitorToken])

  useEffect(() => {
    if (!scriptReady || scriptLoadedRef.current) return

    const portalId = (CONSTANTS as any).HUBSPOT_PORTAL_ID as string | undefined
    if (!portalId || portalId.startsWith('__')) return

    const email = AccessManager.tokenDetails?.email ?? AccessManager.tokenDetails?.sub

    // Set identification settings BEFORE loading the script (only if we have a token)
    if (visitorToken && email) {
      window.hsConversationsSettings = {
        identificationEmail: email,
        identificationToken: visitorToken,
        loadImmediately: true,
      }
    }

    const script = document.createElement('script')
    script.id = 'hs-script-loader'
    script.src = `//js-eu1.hs-scripts.com/${portalId}.js`
    script.async = true
    document.body.appendChild(script)
    scriptLoadedRef.current = true
  }, [scriptReady, visitorToken])

  useEffect(() => {
    function onConversationsReady() {
      if (window.HubSpotConversations) {
        setIsReady(true)
      }
    }

    if (window.HubSpotConversations) {
      onConversationsReady()
    } else {
      window.hsConversationsOnReady = window.hsConversationsOnReady || []
      window.hsConversationsOnReady.push(onConversationsReady)
    }
  }, [])

  const contactSupport = useCallback(() => {
    if (window.HubSpotConversations) {
      const status = window.HubSpotConversations.widget.status()
      if (status.loaded) {
        window.HubSpotConversations.widget.open()
      } else {
        window.HubSpotConversations.widget.load({ widgetOpen: true })
      }
    }
  }, [])

  return <HubspotContext.Provider value={{ contactSupport, isInitialized: isReady }}>{children}</HubspotContext.Provider>
}
